/**
 * Iter133 — Ops Plan Street View Planner
 *
 * Admin-only tactical planning surface:
 *  - Left: Google Map (click to drop a pano location)
 *  - Right: Street View pano for the current viewpoint
 *  - Overlay planning icons (vehicle, person, officer, camera, entry,
 *    exit, hazard, custom) on the pano (drag to reposition)
 *  - Save/load named viewpoints per case
 *  - Shareable deep-link to any viewpoint (/cases/:id/ops-plan/:planId)
 *
 * Rendered standalone on the case → "Ops Plan" tab, and from
 * /cases/:caseId/ops-plan/:planId as a read-only shareable view.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, Marker, StreetViewPanorama } from '@react-google-maps/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { useAuth } from '../../context/AuthContext';
import {
  Save, Trash2, Share2, Plus, MapPin, Car, User, ShieldAlert, Camera, DoorOpen, DoorClosed,
  AlertTriangle, Pencil, X, Loader2, Eye, Users, Crosshair,
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places', 'drawing'];
const defaultCenter = { lat: 32.7767, lng: -96.7970 };

const ICON_KINDS = [
  { kind: 'vehicle',  label: 'Vehicle',  Icon: Car,            color: '#F59E0B' },
  { kind: 'person',   label: 'Subject',  Icon: User,           color: '#EF4444' },
  { kind: 'officer',  label: 'Officer',  Icon: Users,          color: '#3B82F6' },
  { kind: 'camera',   label: 'Camera',   Icon: Camera,         color: '#8B5CF6' },
  { kind: 'entry',    label: 'Entry',    Icon: DoorOpen,       color: '#10B981' },
  { kind: 'exit',     label: 'Exit',     Icon: DoorClosed,     color: '#F97316' },
  { kind: 'hazard',   label: 'Hazard',   Icon: AlertTriangle,  color: '#DC2626' },
  { kind: 'custom',   label: 'Custom',   Icon: Crosshair,      color: '#94A3B8' },
];
const kindMeta = (k) => ICON_KINDS.find(i => i.kind === k) || ICON_KINDS[7];

const streetViewStaticUrl = ({ lat, lng, heading = 0, pitch = 0 }) => {
  if (!lat || !lng || !GOOGLE_MAPS_API_KEY) return null;
  const base = 'https://maps.googleapis.com/maps/api/streetview';
  const params = new URLSearchParams({
    size: '320x180',
    location: `${lat},${lng}`,
    heading: String(heading || 0),
    pitch: String(pitch || 0),
    fov: '90',
    key: GOOGLE_MAPS_API_KEY,
  });
  return `${base}?${params.toString()}`;
};

export default function OpsPlanTab({ caseId, caseData, initialPlanId = null, readOnly: forcedReadOnly = false }) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };
  const { user } = useAuth() || {};
  const isAdmin = ['main_hub', 'department_admin', 'super_admin'].includes(user?.role);
  const readOnly = forcedReadOnly || !isAdmin;

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: LIBRARIES });

  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const panoRef = useRef(null);
  const dragState = useRef(null);
  const paneRef = useRef(null);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/ops-plans`, { headers: H });
      const d = r.ok ? await r.json() : [];
      setPlans(d);
      if (initialPlanId) {
        const found = d.find(p => p.id === initialPlanId);
        if (found) setPlan(found);
      } else if (!plan && d.length > 0) {
        setPlan(d[0]);
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [caseId, initialPlanId]);

  useEffect(() => { loadPlans(); }, [loadPlans]);

  // Build a working plan state (either a loaded one, or a blank template)
  useEffect(() => {
    if (plan) setName(plan.name || '');
  }, [plan?.id]); // eslint-disable-line

  const workingPlan = plan || {
    name: '',
    lat: defaultCenter.lat,
    lng: defaultCenter.lng,
    heading: 0,
    pitch: 0,
    zoom: 1,
    icons: [],
  };

  const mutatePlan = (patch) => {
    setPlan(prev => ({ ...(prev || workingPlan), ...patch }));
  };

  const onMapClick = (e) => {
    if (readOnly) return;
    const lat = e.latLng.lat(), lng = e.latLng.lng();
    mutatePlan({ lat, lng, heading: 0, pitch: 0 });
  };

  const addIcon = (kind) => {
    if (readOnly) return;
    const id = `IC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    const next = [...(workingPlan.icons || []), { id, kind, x_frac: 0.5, y_frac: 0.5, label: '' }];
    mutatePlan({ icons: next });
  };
  const removeIcon = (id) => {
    if (readOnly) return;
    mutatePlan({ icons: (workingPlan.icons || []).filter(i => i.id !== id) });
  };
  const relabelIcon = (id, label) => {
    if (readOnly) return;
    mutatePlan({ icons: (workingPlan.icons || []).map(i => i.id === id ? { ...i, label } : i) });
  };

  // Icon drag over the pano overlay (positioned via x_frac/y_frac)
  const onIconMouseDown = (e, icon) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    dragState.current = { id: icon.id };
    const move = (ev) => {
      const rect = paneRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
      setPlan(prev => prev && ({
        ...prev,
        icons: (prev.icons || []).map(i => i.id === icon.id ? { ...i, x_frac: x, y_frac: y } : i),
      }));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      dragState.current = null;
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Pull current heading/pitch/zoom from the live pano when saving.
  const readPanoState = () => {
    const pano = panoRef.current;
    if (!pano) return {};
    try {
      const pov = pano.getPov?.() || {};
      const pos = pano.getPosition?.();
      return {
        heading: pov.heading || 0,
        pitch: pov.pitch || 0,
        zoom: pano.getZoom?.() || 1,
        lat: pos?.lat?.() ?? workingPlan.lat,
        lng: pos?.lng?.() ?? workingPlan.lng,
      };
    } catch { return {}; }
  };

  const savePlan = async () => {
    if (readOnly) return;
    if (!name.trim()) return toast({ title: 'Give this viewpoint a name', variant: 'destructive' });
    if (!workingPlan.lat || !workingPlan.lng) return toast({ title: 'Pick a location on the map first', variant: 'destructive' });
    const live = readPanoState();
    const payload = {
      name: name.trim(),
      lat: live.lat ?? workingPlan.lat,
      lng: live.lng ?? workingPlan.lng,
      heading: live.heading ?? workingPlan.heading ?? 0,
      pitch: live.pitch ?? workingPlan.pitch ?? 0,
      zoom: live.zoom ?? workingPlan.zoom ?? 1,
      icons: workingPlan.icons || [],
    };
    payload.thumbnail_url = streetViewStaticUrl(payload);

    setSaving(true);
    try {
      if (plan?.id) {
        const r = await fetch(`${BACKEND_URL}/api/ops-plans/${plan.id}`, {
          method: 'PATCH', headers: JH, body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error('save failed');
        const d = await r.json();
        setPlan(d);
      } else {
        const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/ops-plans`, {
          method: 'POST', headers: JH, body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error('save failed');
        const d = await r.json();
        setPlan(d);
      }
      toast({ title: 'Viewpoint saved' });
      await loadPlans();
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const deletePlan = async () => {
    if (readOnly || !plan?.id) return;
    if (!window.confirm(`Delete "${plan.name}"?`)) return;
    const r = await fetch(`${BACKEND_URL}/api/ops-plans/${plan.id}`, { method: 'DELETE', headers: H });
    if (!r.ok) return toast({ title: 'Delete failed', variant: 'destructive' });
    setPlan(null);
    toast({ title: 'Viewpoint deleted' });
    await loadPlans();
  };

  const newPlan = () => {
    if (readOnly) return;
    setPlan(null);
    setName('');
  };

  const sharePlan = () => {
    if (!plan?.id) return toast({ title: 'Save first to share' });
    const url = `${window.location.origin}/cases/${caseId}/ops-plan/${plan.id}`;
    navigator.clipboard?.writeText(url);
    toast({ title: 'Share link copied', description: url });
  };

  const panoCenter = useMemo(
    () => ({ lat: Number(workingPlan.lat) || defaultCenter.lat, lng: Number(workingPlan.lng) || defaultCenter.lng }),
    [workingPlan.lat, workingPlan.lng],
  );

  if (!isLoaded) {
    return (
      <div className="p-6 text-slate-400 text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Street View…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ops-plan-tab">
      {/* Toolbar */}
      <div className="rounded-lg border border-slate-700 bg-[#131A2A] p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Eye className="w-5 h-5 text-blue-300" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Admin · Operation Planner</div>
            <div className="text-sm font-semibold text-white">
              {plan ? `Editing: ${plan.name}` : 'New Viewpoint'}
              {readOnly && <Badge className="ml-2 bg-slate-700 text-slate-300 text-[10px]">READ-ONLY</Badge>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!readOnly && (
            <>
              <Input value={name} onChange={e => setName(e.target.value)}
                placeholder="Viewpoint name (e.g. Front door breach)"
                className="h-8 text-xs bg-slate-900 border-slate-700 text-white w-56"
                data-testid="ops-plan-name" />
              <Button size="sm" onClick={savePlan} disabled={saving}
                className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                data-testid="ops-plan-save">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
              <Button size="sm" variant="outline" onClick={newPlan}
                className="h-8 text-xs border-slate-600 text-slate-200">
                <Plus className="w-3 h-3 mr-1" /> New
              </Button>
              {plan?.id && (
                <Button size="sm" variant="outline" onClick={deletePlan}
                  className="h-8 text-xs border-red-500/50 text-red-300 hover:bg-red-500/10"
                  data-testid="ops-plan-delete">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              )}
            </>
          )}
          <Button size="sm" variant="outline" onClick={sharePlan}
            className="h-8 text-xs border-blue-500/50 text-blue-300 hover:bg-blue-500/10"
            data-testid="ops-plan-share">
            <Share2 className="w-3 h-3 mr-1" /> Share
          </Button>
        </div>
      </div>

      {/* Icon palette */}
      {!readOnly && (
        <div className="rounded-lg border border-slate-700 bg-[#131A2A] p-2 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mr-1">Drop icons:</span>
          {ICON_KINDS.map(({ kind, label, Icon, color }) => (
            <Button key={kind} size="sm" variant="outline"
              onClick={() => addIcon(kind)}
              className="h-7 text-xs border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid={`ops-plan-icon-${kind}`}>
              <Icon className="w-3 h-3 mr-1" style={{ color }} /> {label}
            </Button>
          ))}
        </div>
      )}

      {/* Split view */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Map */}
        <div className="rounded-lg border border-slate-700 bg-[#131A2A] overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-200">
            Plan Map {readOnly ? '' : '· click to re-center the pano'}
          </div>
          <div style={{ height: 420 }}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={panoCenter}
              zoom={18}
              options={{ mapTypeId: 'hybrid', tilt: 0 }}
              onClick={onMapClick}
            >
              <Marker position={panoCenter} />
            </GoogleMap>
          </div>
        </div>

        {/* Pano with icon overlay */}
        <div className="rounded-lg border border-slate-700 bg-[#131A2A] overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-200">
            Street View {readOnly ? '' : '· drag icons to position'}
          </div>
          <div style={{ position: 'relative', height: 420 }} ref={paneRef}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={panoCenter}
              zoom={18}
              options={{ streetViewControl: false, disableDefaultUI: true }}
            >
              <StreetViewPanorama
                position={panoCenter}
                visible={true}
                options={{
                  pov: { heading: workingPlan.heading || 0, pitch: workingPlan.pitch || 0 },
                  zoom: workingPlan.zoom || 1,
                  enableCloseButton: false,
                  addressControl: true,
                  fullscreenControl: true,
                }}
                onLoad={(p) => { panoRef.current = p; }}
              />
            </GoogleMap>

            {/* Icon overlay */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {(workingPlan.icons || []).map(icon => {
                const meta = kindMeta(icon.kind);
                const Icon = meta.Icon;
                return (
                  <div key={icon.id}
                    onMouseDown={(e) => onIconMouseDown(e, icon)}
                    style={{
                      position: 'absolute',
                      left: `${(icon.x_frac || 0.5) * 100}%`,
                      top: `${(icon.y_frac || 0.5) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'auto',
                      cursor: readOnly ? 'default' : 'move',
                    }}
                    data-testid={`ops-plan-overlay-icon-${icon.id}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                           style={{ backgroundColor: meta.color }}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      {icon.label && (
                        <div className="mt-0.5 text-[10px] px-1.5 py-0.5 bg-slate-900/80 text-white rounded font-mono whitespace-nowrap">
                          {icon.label}
                        </div>
                      )}
                      {!readOnly && (
                        <div className="mt-0.5 flex items-center gap-1">
                          <input value={icon.label || ''} onChange={e => relabelIcon(icon.id, e.target.value)}
                            placeholder="label"
                            className="h-5 w-20 text-[10px] bg-slate-900/80 border border-slate-700 text-white px-1 rounded" />
                          <button onClick={() => removeIcon(icon.id)} className="text-red-300 hover:text-red-200">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Saved viewpoints */}
      <div className="rounded-lg border border-slate-700 bg-[#131A2A]">
        <div className="px-3 py-2 border-b border-slate-700 text-xs font-mono uppercase tracking-wider text-slate-200 flex items-center justify-between">
          <span>Saved Viewpoints</span>
          <span className="text-slate-500">{plans.length}</span>
        </div>
        {loading ? (
          <div className="p-4 text-slate-500 text-xs">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="p-4 text-slate-500 text-xs text-center">
            No viewpoints yet. {!readOnly && 'Click a spot on the map, drop icons, name it, and press Save.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 p-3">
            {plans.map(p => (
              <button key={p.id}
                onClick={() => setPlan(p)}
                className={`text-left rounded border overflow-hidden hover:border-blue-400 transition ${plan?.id === p.id ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-700'}`}
                data-testid={`ops-plan-card-${p.id}`}>
                <div className="aspect-video bg-slate-800 relative">
                  {p.thumbnail_url ? (
                    <img src={p.thumbnail_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">No preview</div>
                  )}
                  <div className="absolute top-1 right-1 bg-slate-900/80 text-white text-[9px] px-1.5 py-0.5 rounded font-mono">
                    {(p.icons || []).length} icons
                  </div>
                </div>
                <div className="px-2 py-1.5">
                  <div className="text-xs font-semibold text-white truncate">{p.name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    by {p.created_by_name || 'admin'} · {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
